import { useCallback } from 'react';
import { ID } from '@/model/Common.model.ts';
import { Measurement } from '@/model/Measurement.model.ts';
import { ApiService } from '@/support/ApiService.ts';
import { useUpdateMeasurements } from './useWorkflow.tsx';
import { notAborted, useAbortController } from '@/support/Hooks/fetch/useAbortController.ts';
import { MeasurementsError } from '@/utils/workflow/WorkflowProvider.tsx';

interface UseMeasurementActionsResponse {
  fetchMeasurements: (animalId: ID) => Promise<void>;
  fetchTodaysMeasurement: (animalId: ID, measuredAt: string) => Promise<void>;
  clearMeasurements: (animalId?: ID) => void;
  updateSingleMeasurement: (animalId: ID, measurement: Measurement) => void;
}

/**
 * Custom hook that provides centralized measurement actions for the workflow provider.
 * Handles all measurement data fetching, caching, and state management.
 */
export const useMeasurementActions = (): UseMeasurementActionsResponse => {
  const { updateMeasurements } = useUpdateMeasurements();
  const { newAbortController: newMeasurementsAbortController } = useAbortController();
  const { newAbortController: newTodaysMeasurementAbortController } = useAbortController();

  const fetchMeasurements = useCallback(
    async (animalId: ID) => {
      // Set loading state
      updateMeasurements({
        type: 'SET_MEASUREMENTS_LOADING',
        animalId,
        loading: true,
      });

      // Clear any previous errors
      updateMeasurements({
        type: 'SET_MEASUREMENTS_ERROR',
        animalId,
        error: null,
      });

      try {
        const response = await ApiService.call({
          endpoint: 'GET /api/v1/animals/{animalId}/measurements',
          path: { animalId: String(animalId) },
          signal: newMeasurementsAbortController().signal,
        });

        if (response.type === 'success') {
          const measurements = response.body.data as unknown as Array<Measurement>;
          updateMeasurements({
            type: 'SET_MEASUREMENTS',
            animalId,
            measurements,
          });
        } else {
          updateMeasurements({
            type: 'SET_MEASUREMENTS_ERROR',
            animalId,
            error: response,
          });
        }
      } catch (error) {
        if (notAborted(error)) {
          updateMeasurements({
            type: 'SET_MEASUREMENTS_ERROR',
            animalId,
            error: error as MeasurementsError,
          });
        }
      } finally {
        updateMeasurements({
          type: 'SET_MEASUREMENTS_LOADING',
          animalId,
          loading: false,
        });
      }
    },
    [updateMeasurements]
  );

  const fetchTodaysMeasurement = useCallback(
    async (animalId: ID, measuredAt: string) => {
      // Set loading state
      updateMeasurements({
        type: 'SET_MEASUREMENTS_LOADING',
        animalId,
        loading: true,
      });

      try {
        const response = await ApiService.call({
          endpoint: 'GET /api/v1/animals/{animalId}/measurements',
          path: { animalId: String(animalId) },
          query: { measured_at: measuredAt },
          signal: newTodaysMeasurementAbortController().signal,
        });

        if (response.type === 'success') {
          const measurements = response.body.data as unknown as Array<Measurement>;
          const todaysMeasurement = measurements?.[0] || null;
          updateMeasurements({
            type: 'SET_TODAYS_MEASUREMENT',
            animalId,
            measurement: todaysMeasurement,
          });
        } else {
          updateMeasurements({
            type: 'SET_MEASUREMENTS_ERROR',
            animalId,
            error: response,
          });
        }
      } catch (error) {
        if (notAborted(error)) {
          updateMeasurements({
            type: 'SET_MEASUREMENTS_ERROR',
            animalId,
            error: error as MeasurementsError,
          });
        }
      } finally {
        updateMeasurements({
          type: 'SET_MEASUREMENTS_LOADING',
          animalId,
          loading: false,
        });
      }
    },
    [updateMeasurements]
  );

  const clearMeasurements = useCallback(
    (animalId?: ID) => {
      updateMeasurements({
        type: 'CLEAR_MEASUREMENTS',
        animalId,
      });
    },
    [updateMeasurements]
  );

  const updateSingleMeasurement = useCallback(
    (animalId: ID, measurement: Measurement) => {
      updateMeasurements({
        type: 'UPDATE_SINGLE_MEASUREMENT',
        animalId,
        measurement,
      });
    },
    [updateMeasurements]
  );

  return {
    fetchMeasurements,
    fetchTodaysMeasurement,
    clearMeasurements,
    updateSingleMeasurement,
  };
};
